import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { StudentAchievement } from '../../types/achievement';
import AchievementCard from './AchievementCard';

interface AchievementDialogProps {
  open: boolean;
  onClose: () => void;
  newAchievements: StudentAchievement[];
  onViewTrophyCase: () => void;
}

/**
 * A dialog that displays newly earned achievements with celebration styling
 */
const AchievementDialog: React.FC<AchievementDialogProps> = ({
  open,
  onClose,
  newAchievements,
  onViewTrophyCase
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #3f51b5 0%, #303f9f 100%)',
          color: 'white',
          py: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background pattern */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          opacity: 0.1, 
          background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          zIndex: 0
        }} />
        
        {/* Light beam effect */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '50%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 60%)',
            transform: 'rotate(35deg)',
            zIndex: 0
          }}
        />
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2,
            position: 'relative',
            zIndex: 1
          }}
        >
          <Box 
            sx={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              p: 1.5,
              boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.1)' },
                '100%': { transform: 'scale(1)' },
              }
            }}
          >
            <TrophyIcon sx={{ color: '#FFD700', fontSize: 36 }} />
          </Box>
          <Typography 
            variant="h5" 
            component="span" 
            sx={{ 
              fontWeight: 800,
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              fontSize: '1.5rem'
            }}
          >
            Achievement Unlocked!
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ py: 3 }}>
        {newAchievements.length > 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Congratulations! You've earned {newAchievements.length > 1 ? 'new achievements' : 'a new achievement'}:
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2,
                mt: 3 
              }}
            >
              {newAchievements.map((studentAchievement) => (
                studentAchievement.achievement ? (
                  <Box key={studentAchievement.id} sx={{ mb: 2 }}>
                    <AchievementCard 
                      achievement={studentAchievement.achievement}
                      earned={true}
                      earnedDate={studentAchievement.awardedAt}
                    />
                  </Box>
                ) : null
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions 
        sx={{ 
          p: 3, 
          justifyContent: 'center',
          borderTop: '1px solid rgba(0,0,0,0.09)',
          gap: 2,
          background: 'linear-gradient(180deg, #f8f9ff 0%, #f0f2ff 100%)'
        }}
      >
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          size="large"
          sx={{
            borderRadius: '30px',
            px: 3,
            py: 1.2,
            boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)',
            fontWeight: 600,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: '0 6px 15px rgba(63, 81, 181, 0.4)'
            }
          }}
        >
          Continue Learning
        </Button>
        <Button 
          onClick={onViewTrophyCase} 
          variant="outlined" 
          color="primary"
          size="large"
          endIcon={<TrophyIcon />}
          sx={{
            borderRadius: '30px',
            px: 3,
            py: 1.2,
            borderWidth: 2,
            fontWeight: 600,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              borderWidth: 2,
              backgroundColor: 'rgba(63, 81, 181, 0.04)'
            }
          }}
        >
          View Trophy Case
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AchievementDialog;